<template>
  <article class="bootcamp-tile">
    <div
      class="bootcamp-tile__static-wrapper"
      :style="`background-image: url(${bootcamp.background_image_url})`"
    >
      <div class="bootcamp-tile__pass">
        <Passes :passName="learningAreaName" :size="45" />
      </div>
      <div class="bootcamp__instructors-profile-imgs">
        <img
          v-for="instructor in limitedInstructors"
          :key="instructor.id"
          :src="getInstructorImg(instructor)"
          class="bootcamp__instructor-img"
          :alt="getInstructorName(instructor)"
        />
      </div>
      <div
        class="bootcamp__instructors-profile-imgs-plus"
        v-if="bootcamp.instructors.length > 2"
      >
        + {{ bootcamp.instructors.length - 2 }}
      </div>
    </div>
    <div class="bootcamp-tile__info-wrapper">
      <div class="bootcamp-tile__info">
        <h4 class="bootcamp-tile__title">
          <router-link
            :to="{ name: 'bootcamp', params: { id: bootcampId } }"
            class="bootcamp-tile__title-link"
            >{{ bootcamp.name }}</router-link
          >
        </h4>
        <p class="bootcamp-tile__instructors">With {{ contentInstructors }}</p>
        <p
          class="bootcamp-tile__description"
          v-html="renderMarkdown(bootcamp.description)"
        ></p>
      </div>
      <div class="bootcamp-tile__details">
        <div class="bootcamp-tile__detail-wrapper">
          <p class="bootcamp-tile__detail bootcamp__date">
            {{ formatDateForBootcamp }}
            <i
              class="bootcamp-tile__timezone-icon material-icons info-icon"
              role="presentation"
              v-tooltip="{
                content: `Local time: ${localTimeZone}`,
                placement: 'top-center',
              }"
            >
              info_outline
            </i>
          </p>
        </div>
        <div class="bootcamp-tile__actions">
          <router-link
            :to="{ name: 'bootcamp', params: { id: bootcampId } }"
            class="bootcamp-tile__link btn btn--secondary"
            >Learn more</router-link
          >
        </div>
      </div>
    </div>
  </article>
</template>

<script>
import { format } from 'date-fns-tz'
import Passes from '@/components/properties/Passes.vue'
import { localTimezone, bootcampCommons } from '@/helpers/mixins'

export default {
  name: 'BootcampTile',
  mixins: [localTimezone, bootcampCommons],
  components: {
    Passes,
  },
  props: {
    bootcamp: {
      type: Object,
      required: true,
    },
    renderMarkdown: {
      type: Function,
      required: true,
    },
    epoch: {
      type: Boolean,
      required: false,
      default: () => {
        return false
      },
    },
  },
  computed: {
    bootcampId() {
      return this.bootcamp.id || this.bootcamp.objectID
    },
    learningAreaName() {
      const {
        bootcamp: { learning_areas },
      } = this
      return learning_areas && learning_areas[0] && learning_areas[0].name
        ? learning_areas[0].name
        : 'IT Essentials'
    },
    formatDateForBootcamp() {
      const {
        bootcamp: { start_datetime: start, end_datetime: end },
        epoch,
      } = this
      const startDate = epoch ? new Date(start * 1000) : new Date(start)
      const endDate = epoch ? new Date(end * 1000) : new Date(end)
      return `${format(startDate, 'MMMM do')} - ${format(endDate, 'MMMM do')}`
    },
  },
}
</script>
<style lang="scss" scoped>
.bootcamp-tile {
  display: flex;
  align-items: stretch;
  flex-wrap: wrap;
  width: 100%;
  background-color: var(--darker-gray);
  position: relative;
  box-shadow: 0px 0px 8px #0000000a;
  border-radius: 5px;
  min-height: 290px;
  margin: 0 0 15px 0;
  padding-bottom: 15px;
  &__static-wrapper {
    position: relative;
    background: var(--darker-gray);
    width: 100%;
    display: block;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    min-height: 150px;
  }
  &__pass {
    position: absolute;
    left: 30px;
    top: 25px;
  }
  &__instructor-img {
    max-height: 70px;
    border-radius: 70px;
    position: absolute;
    left: 30px;
    top: 110px;
  }
  &__instructor-with {
    font-weight: bold;
  }
  &__info-wrapper {
    color: var(--white);
    padding: 30px;
    display: flex;
    flex-direction: column;
    position: relative;
    width: 100%;
  }
  &__info {
    margin: 0 0 30px 0;
    @include tablet {
      padding: 0;
    }
  }
  &__title {
    font-family: var(--font-bold);
    font-size: rem(20px);
    padding: 0;
    margin: 10px 0 5px 0;
    width: 100%;
    @include tablet {
      max-width: 615px;
    }
    &-link {
      text-decoration: none;
      color: var(--white);
    }
    &-link:hover {
      color: color(--white);
    }
  }
  &__description {
    padding: 0;
    margin: 0 0 25px 0;
    display: block;
    overflow-wrap: break-word;
    word-wrap: break-word;
    overflow: hidden;
    max-height: 5.6em;
    line-height: 1.5em;
    position: relative;
    a {
      color: var(--white);
      text-decoration: underline;
      &:hover {
        color: var(--orange);
      }
    }
    &:after {
      content: '';
      height: 20px;
      width: 100%;
      position: absolute;
      left: 0;
      bottom: 0;
      background: linear-gradient(
        0deg,
        rgba(2, 0, 36, 1) 0%,
        rgba(34, 34, 34, 1) 0%,
        rgba(255, 255, 255, 0) 100%
      );
    }
  }
  &__premium-cta {
    max-width: 180px;
    align-items: center;
    display: flex;
  }
  &__details {
    margin: auto 0 0 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    width: 100%;
    @include tablet {
      flex-wrap: nowrap;
    }
  }
  &__detail-wrapper {
    display: flex;
    margin: 0 auto 0 0;
    width: 100%;
    @include tablet {
      width: auto;
      max-width: 615px;
    }
  }
  &__detail {
    padding: 0;
    margin: 0 auto 0 0;
    display: flex;
    align-items: center;
    font-size: rem(15px);
    &:nth-of-type(1) {
      margin: 0 25px 0 0;
    }
  }
  &__date {
    max-width: 420px;
  }
  &__icon {
    fill: var(--lighter-gray);
    margin: 0 5px 0 0;
    min-width: 30px;
  }
  &__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin: 50px 25px 0 auto;
    @include desktop {
      width: 30%;
      margin: 0 0 0 auto;
    }
  }
  &__link {
    margin: auto 25px 0 0;
    @include tablet {
      margin: auto 25px 0 auto;
    }
  }
  &__timezone-icon {
    font-size: 16px !important;
    margin-left: 5px;
    color: var(--gray);
  }
  .bootcamp {
    &__instructor-img {
      right: auto;
      top: 115px;
      left: 30px;
      width: 60px;
    }
    &__instructors-profile-imgs {
      img:nth-child(2) {
        left: 80px;
      }
      img:nth-child(1) {
        z-index: 1;
      }
    }
    &__instructors-profile-imgs-plus {
      position: absolute;
      left: 130px;
      top: 115px;
      right: auto;
    }
  }
}
</style>
